import { useQuery } from "@tanstack/react-query";
import createPrivateAdminPanelInstance from "../../api/PrivateAdminPanelInstance";
import { API_ROUTES } from "../../api/ApiRoutes";
import { getAccessToken } from "../../api/getAccessToken";
import {
  DEFAULT_PAGE_CODE,
  fetchAdminViewPlacements,
} from "../casino-configuration/views/useAdminViews";

export type CasinoDeeplinkSearchType =
  | "game"
  | "provider"
  | "collection"
  | "category";

export type CasinoDeeplinkDropdownItem = {
  id: string;
  name: string;
  /** Games only — written to deeplink `provider_id` on select. */
  provider?: string;
};

type ApiListResponse<T> = {
  success?: boolean;
  data?: {
    items?: T[];
    total?: number;
    meta?: { total?: number };
  };
};

type GameApiItem = {
  game_id: string;
  provider?: string;
  name?: string;
  game_name?: string;
};

type ProviderApiItem = {
  provider_id: string;
  name?: string;
};

/** Lobby carousel placements → casino_collection destinations. */
const COLLECTION_VIEW_KEY = "carousels";
/** Lobby shortcut placements → casino_category destinations. */
const CATEGORY_VIEW_KEY = "shortcuts";

const SEARCH_LIMIT = 20;
const GAMES_PAGE_SIZE = 20;
const PROVIDERS_PAGE_SIZE = 50;

function getInstance() {
  return createPrivateAdminPanelInstance(getAccessToken());
}

function normalizeGameItems(data: unknown[]): CasinoDeeplinkDropdownItem[] {
  return (data as GameApiItem[])
    .filter((item) => item?.game_id)
    .map((item) => ({
      id: item.game_id,
      name: item.name || item.game_name || item.game_id,
      ...(item.provider ? { provider: item.provider } : {}),
    }))
    .sort((a, b) => a.name.localeCompare(b.name))
    .slice(0, SEARCH_LIMIT);
}

function normalizeProviderItems(data: unknown[]): CasinoDeeplinkDropdownItem[] {
  return (data as ProviderApiItem[])
    .filter((item) => item?.provider_id)
    .map((item) => ({
      id: item.provider_id,
      name: item.name || item.provider_id,
    }))
    .sort((a, b) => a.name.localeCompare(b.name))
    .slice(0, SEARCH_LIMIT);
}

function normalizeViewPlacementItems(
  placements: Awaited<ReturnType<typeof fetchAdminViewPlacements>>["items"],
): CasinoDeeplinkDropdownItem[] {
  return placements
    .filter((placement) => placement.is_active && placement.collection?.code)
    .map((placement) => ({
      id: placement.collection.code,
      name: placement.collection.name || placement.collection.code,
    }))
    .sort((a, b) => a.name.localeCompare(b.name))
    .slice(0, SEARCH_LIMIT);
}

async function fetchCasinoGameDropdown(
  search: string,
): Promise<CasinoDeeplinkDropdownItem[]> {
  const instance = getInstance();
  const params: Record<string, string | number | boolean> = {
    page: 1,
    page_size: GAMES_PAGE_SIZE,
    active_only: true,
  };
  if (search.trim()) params.search = search.trim();

  const response = await instance.get(API_ROUTES.GET_ADMIN_GAMES, { params });
  const body = response?.data as ApiListResponse<GameApiItem> | undefined;
  const items = Array.isArray(body?.data?.items) ? body.data.items : [];
  return normalizeGameItems(items);
}

async function fetchCasinoProviderDropdown(
  search: string,
): Promise<CasinoDeeplinkDropdownItem[]> {
  const instance = getInstance();
  const params: Record<string, string | number | boolean> = {
    page: 1,
    page_size: PROVIDERS_PAGE_SIZE,
    active_only: true,
  };
  if (search.trim()) params.search = search.trim();

  const response = await instance.get(API_ROUTES.GET_ADMIN_PROVIDERS, { params });
  const body = response?.data as ApiListResponse<ProviderApiItem> | undefined;
  const items = Array.isArray(body?.data?.items) ? body.data.items : [];
  return normalizeProviderItems(items);
}

async function fetchCasinoViewPlacementDropdown(
  view: typeof COLLECTION_VIEW_KEY | typeof CATEGORY_VIEW_KEY,
  search: string,
): Promise<CasinoDeeplinkDropdownItem[]> {
  const result = await fetchAdminViewPlacements(
    view,
    search.trim() || undefined,
    DEFAULT_PAGE_CODE,
  );
  return normalizeViewPlacementItems(result.items);
}

export const CASINO_DEEPLINK_DROPDOWN_QUERY_KEY =
  "CASINO_DEEPLINK_DROPDOWN" as const;

export function useCasinoGameDeeplinkDropdown(
  search: string,
  enabled = true,
) {
  return useQuery<CasinoDeeplinkDropdownItem[], Error>({
    queryKey: [CASINO_DEEPLINK_DROPDOWN_QUERY_KEY, "game", search],
    queryFn: () => fetchCasinoGameDropdown(search),
    enabled,
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
    placeholderData: (prev) => prev,
  });
}

export function useCasinoProviderDeeplinkDropdown(
  search: string,
  enabled = true,
) {
  return useQuery<CasinoDeeplinkDropdownItem[], Error>({
    queryKey: [CASINO_DEEPLINK_DROPDOWN_QUERY_KEY, "provider", search],
    queryFn: () => fetchCasinoProviderDropdown(search),
    enabled,
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
    placeholderData: (prev) => prev,
  });
}

/** Carousel placements for `casino_collection` destinations. */
export function useCasinoCollectionDeeplinkDropdown(
  search: string,
  enabled = true,
) {
  return useQuery<CasinoDeeplinkDropdownItem[], Error>({
    queryKey: [
      CASINO_DEEPLINK_DROPDOWN_QUERY_KEY,
      "collection",
      COLLECTION_VIEW_KEY,
      DEFAULT_PAGE_CODE,
      search,
    ],
    queryFn: () =>
      fetchCasinoViewPlacementDropdown(COLLECTION_VIEW_KEY, search),
    enabled,
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
    placeholderData: (prev) => prev,
  });
}

/** Shortcut placements for `casino_category` destinations. */
export function useCasinoCategoryDeeplinkDropdown(
  search: string,
  enabled = true,
) {
  return useQuery<CasinoDeeplinkDropdownItem[], Error>({
    queryKey: [
      CASINO_DEEPLINK_DROPDOWN_QUERY_KEY,
      "category",
      CATEGORY_VIEW_KEY,
      DEFAULT_PAGE_CODE,
      search,
    ],
    queryFn: () =>
      fetchCasinoViewPlacementDropdown(CATEGORY_VIEW_KEY, search),
    enabled,
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
    placeholderData: (prev) => prev,
  });
}

export function useCasinoDeeplinkDropdown(
  searchType: CasinoDeeplinkSearchType,
  search: string,
  enabled = true,
) {
  const gameQuery = useCasinoGameDeeplinkDropdown(
    search,
    enabled && searchType === "game",
  );
  const providerQuery = useCasinoProviderDeeplinkDropdown(
    search,
    enabled && searchType === "provider",
  );
  const collectionQuery = useCasinoCollectionDeeplinkDropdown(
    search,
    enabled && searchType === "collection",
  );
  const categoryQuery = useCasinoCategoryDeeplinkDropdown(
    search,
    enabled && searchType === "category",
  );

  switch (searchType) {
    case "game":
      return gameQuery;
    case "provider":
      return providerQuery;
    case "collection":
      return collectionQuery;
    case "category":
      return categoryQuery;
  }
}
